import toast from 'react-hot-toast';
import { useAppContext } from '../contexts/AppContext';
import { useFormContext } from '../contexts/FormContext';
import { isFieldHidden } from '../components/form-engine/fieldVisibility';
import type { FieldSchema, PageSchema } from '../components/form-engine/types';
import { getSoilingBlockKeys } from '../utils/soilingSensor';
import { validateRequiredFields, type RequiredField } from '../utils/validationSchema';

export const useProjectValidation = (pages: PageSchema[]) => {
  const { completed, activeStep } = useAppContext();
  const { getValues, getFormDataItem } = useFormContext();

  const checkCompleted = (requiredSteps: number[] | undefined): boolean => {
    let disabled = false;
    requiredSteps?.forEach((requiredStep) => {
      if (!Object.prototype.hasOwnProperty.call(completed, requiredStep) || !completed[requiredStep]) {
        disabled = true;
      }
    });
    return disabled;
  };

  const findRequiredFields = (fields: FieldSchema[], formData: Record<string, unknown>): RequiredField[] => {
    const requiredFields: RequiredField[] = [];

    const processField = (field: FieldSchema) => {
      const isHidden = isFieldHidden(field, formData, getFormDataItem);
      const isDisabled = Object.prototype.hasOwnProperty.call(field, 'disabled')
        ? getFormDataItem(formData, field.disabled as string)
        : false;
      const conditionallyRequired = field.requiredWhenFalse && !getFormDataItem(formData, field.requiredWhenFalse);

      if ((field.required || conditionallyRequired) && field.name && !isHidden && !isDisabled) {
        if (field.name === 'soiling_sensor_data.name' || field.name === 'soiling_sensor_data.type') {
          const blockKeys = getSoilingBlockKeys(formData);
          if (blockKeys.length > 0) {
            blockKeys.forEach((bk) =>
              requiredFields.push({
                name: `${bk}.${field.name!.split('.').pop()}`,
                label: field.label!,
                default: field.default,
                type: field.type,
                min: field.min,
                max: field.max,
              }),
            );
          }
        } else {
          requiredFields.push({
            name: field.name,
            label: field.label!,
            default: field.default,
            type: field.type,
            min: field.min,
            max: field.max,
          });
        }
      }

      if (field.fields && !isHidden && !isDisabled) {
        field.fields.forEach(processField);
      }
    };

    fields.forEach(processField);
    return requiredFields;
  };

  const validateCurrentPage = (): { ok: boolean; message?: string } => {
    const currentPage = pages[activeStep];
    if (!currentPage) {
      return { ok: true };
    }

    const formData = getValues();
    const requiredFields = findRequiredFields(currentPage.fields, formData);
    return validateRequiredFields(requiredFields, formData, getFormDataItem);
  };

  const isFormValid = (): boolean => validateCurrentPage().ok;

  const isValidForm = (): boolean => {
    const { ok, message } = validateCurrentPage();
    if (!ok && message) {
      toast.error(message);
    }
    return ok;
  };

  const validateFields = (form: { fields?: FieldSchema[] } | undefined): boolean => {
    const formData = getValues();
    const walk = (f: { fields?: FieldSchema[] } | undefined): boolean => {
      let valid = true;
      f?.fields?.forEach((field) => {
        if (['text', 'number'].includes(field.type) && !field.fields && !getFormDataItem(formData, field.name!)) {
          valid = false;
        }
        if (Object.prototype.hasOwnProperty.call(field, 'fields')) {
          valid = valid && walk(field);
        }
      });
      return f !== undefined && valid;
    };
    return walk(form);
  };

  return { checkCompleted, findRequiredFields, isFormValid, isValidForm, validateFields };
};
